import { createContext, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { db, firebaseApp } from "../lib/firebase";
import { doc, getDoc } from "firebase/firestore";

const auth = getAuth(firebaseApp);

var ActionType;
(function (ActionType) {
  ActionType["AUTH_STATE_CHANGED"] = "AUTH_STATE_CHANGED";
})(ActionType || (ActionType = {}));

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === "AUTH_STATE_CHANGED") {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  }

  return state;
};

export const AuthContext = createContext({
  ...initialState,
  platform: "Firebase",
  createUserWithEmailAndPassword: () => Promise.resolve(),
  signInWithEmailAndPassword: () => Promise.resolve(),
  signInWithGoogle: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(
    () =>
      onAuthStateChanged(auth, (user) => {
        async function getUserRoleInfo() {
          // Verifica si el usuario se encuentra rgeistrado en la base de datos en "agente" o "distribuidora" o "administrador"
          const agenteRef = await getDoc(doc(db, "agente", user?.uid));

          if (agenteRef.exists()) {
            switch (agenteRef.data().role) {
              case "administrador":
                return {
                  role: "administrador",
                  distribuidora: "administrador",
                  distribuidoraId: "administrador",
                };
              case "distribuidora":
                return {
                  role: "distribuidora",
                  distribuidora: agenteRef.data().distribuidora.name,
                  distribuidoraId: agenteRef.data().distribuidora.id,
                };
              case "agente":
                return {
                  role: "agente",
                  distribuidora: agenteRef.data().distribuidora.name,
                  distribuidoraId: agenteRef.data().distribuidora.id,
                };
              case "callcenter":
                return {
                  role: "callcenter",
                  distribuidora: "callcenter",
                  distribuidoraId: "callcenter",
                };
              case "callcenteradmin":
                return {
                  role: "callcenteradmin",
                  distribuidora: "callcenter",
                  distribuidoraId: "callcenter",
                };
              case "marketing":
                return {
                  role: "marketing",
                  distribuidora: agenteRef.data().distribuidora.name,
                  distribuidoraId: agenteRef.data().distribuidora.id,
                };
              case "finance":
                return {
                  role: "finance",
                  distribuidora: "finance",
                  distribuidoraId: "finance",
                };
              case "financeadmin":
                return {
                  role: "financeadmin",
                  distribuidora: "finance",
                  distribuidoraId: "finance",
                };

              default:
                return {
                  role: "agente",
                  distribuidora: "Distribuidora por defecto 1",
                  distribuidoraId: "distribuidora",
                };
            }
          } else {
            // Cerrar sesión si el usuario no se encuentra registrado en la base de datos
            await signOut(auth);
          }
        }

        if (user) {
          // Here you should extract the complete user profile to make it available in your entire app.
          // The auth state only provides basic information.

          getUserRoleInfo()
            .then((info) => {
              dispatch({
                type: ActionType.AUTH_STATE_CHANGED,
                payload: {
                  isAuthenticated: true,
                  user: {
                    uid: user?.uid,
                    id: user?.uid,
                    avatar: user?.photoURL || undefined,
                    email: user?.email || "error@error.temp",
                    name: user?.displayName || "Unknown",
                    phone: user?.phoneNumber || "+52",
                    role: info.role,
                    distribuidora: info.distribuidora,
                    distribuidoraId: info.distribuidoraId,
                  },
                },
              });
            })
            .catch((error) => {
              console.log(error);
            });
        } else {
          dispatch({
            type: ActionType.AUTH_STATE_CHANGED,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      }),
    [dispatch],
  );

  const _signInWithEmailAndPassword = async (email, password) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();

    await signInWithPopup(auth, provider);
  };

  const _createUserWithEmailAndPassword = async (email, password) => {
    await createUserWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    await signOut(auth);
  };

  // Modificar el rol del state
  const changeRole = (role) => {
    dispatch({
      type: ActionType.AUTH_STATE_CHANGED,
      payload: {
        isAuthenticated: true,
        user: {
          uid: state.user?.uid,
          id: state.user?.uid,
          avatar: state.user?.avatar || undefined,
          email: state.user?.email || "error@error.temp",
          name: state.user?.name || "Unknown",
          phone: state.user?.phoneNumber || "+52",
          role: role,
        },
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: "Firebase",
        createUserWithEmailAndPassword: _createUserWithEmailAndPassword,
        signInWithEmailAndPassword: _signInWithEmailAndPassword,
        signInWithGoogle,
        logout,
        changeRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const AuthConsumer = AuthContext.Consumer;
